import { useChannelContext } from "client/components/channel"
import { useFullscreenContext } from "client/components/layout/FullscreenContext"

import { useVideoContext } from "../../player/VideoContext"

import {
  MaximizeMinimizeFullscreen,
  MuteUnmute,
  PlayPause,
  PlayPauseProps,
  Subtitles,
  SubtitlesProps,
} from "./controlButtons"
import {
  PlaybackScrubBar,
  PlaybackScrubBarProps,
} from "./PlaybackScrubBar"
import { Timestamp } from "./Timestamp"

import styles from "./DesktopVideoControls.module.css"

export type DesktopVideoControlsUIProps = {
  isMuted: boolean
  setMuted: (isMuted: boolean) => void

  isFullscreenAvailable: boolean
  isFullscreen: boolean
  enterFullscreen: () => void
  exitFullscreen: () => void

  // If supplied, this component will render the play/pause button.
  playPauseProps?: PlayPauseProps

  // If supplied, this component will render a timestamp + scrubber.
  scrubBarProps?: PlaybackScrubBarProps

  // If supplied, this component will render the subtitles button.
  subtitlesProps?: SubtitlesProps
}

// This is a 'dumb' UI component that doesn't include any behaviour.
// It exists mostly so we can have a storybook entry for it.
export const DesktopVideoControlsUI = ({
  isMuted,
  setMuted,
  isFullscreenAvailable,
  isFullscreen,
  enterFullscreen,
  exitFullscreen,
  playPauseProps,
  scrubBarProps,
  subtitlesProps,
}: DesktopVideoControlsUIProps) => {
  return (
    <div className={styles.desktopVideoControlsRow}>
      <div className={styles.desktopVideoControlsGroup}>
        {playPauseProps && <PlayPause {...playPauseProps} />}

        {scrubBarProps && (
          <Timestamp currentTime={scrubBarProps.currentTime} />
        )}
      </div>
      {scrubBarProps ? (
        <PlaybackScrubBar {...scrubBarProps} />
      ) : (
        <div style={{ flexGrow: 1 }} />
      )}
      <div className={styles.desktopVideoControlsGroup}>
        {subtitlesProps && <Subtitles {...subtitlesProps} />}
        <MuteUnmute isMuted={isMuted} setMuted={setMuted} />
        {isFullscreenAvailable && (
          <MaximizeMinimizeFullscreen
            isFullscreen={isFullscreen}
            enterFullscreen={enterFullscreen}
            exitFullscreen={exitFullscreen}
          />
        )}
      </div>
    </div>
  )
}

// This component hooks up DesktopVideoControlsUI with our app's state.
export const DesktopVideoControls = () => {
  const { recordingId, liveSubtitles } = useChannelContext()
  const {
    videoElement,
    isPlaying,
    videoTimestamp,
    subtitlesVisible,
    setSubtitlesVisible,
    isMuted,
  } = useVideoContext()

  const {
    isFullscreen,
    isFullscreenAvailable,
    enterFullscreen,
    exitFullscreen,
  } = useFullscreenContext()

  // Subtitles are available for VODs, or for live video with subtitles enabled
  const shouldShowSubtitlesButton = recordingId || liveSubtitles

  return (
    <DesktopVideoControlsUI
      isMuted={isMuted}
      setMuted={(muted) => {
        if (videoElement) {
          videoElement.muted = muted
        }
      }}
      isFullscreenAvailable={isFullscreenAvailable}
      isFullscreen={isFullscreen}
      enterFullscreen={enterFullscreen}
      exitFullscreen={exitFullscreen}
      playPauseProps={
        /* We don't show the play/pause button while live (and unpaused), only because
         * there's a playback freeze bug on android Chrome when unpausing a live video.
         * https://github.com/lazarentertainment/broadcast-viewer/issues/2476
         * if we figure that out maybe we can re-enable play/pause while live.
         */
        recordingId || !isPlaying
          ? {
              isPlaying,
              play: () => {
                videoElement?.play()
              },
              pause: () => {
                videoElement?.pause()
              },
            }
          : undefined
      }
      scrubBarProps={
        recordingId
          ? {
              currentTime: videoTimestamp,
              duration: videoElement?.duration ?? 0,
              onCurrentTimeChange: (newTime) => {
                if (videoElement) {
                  videoElement.currentTime = newTime
                }
              },
            }
          : undefined
      }
      subtitlesProps={
        shouldShowSubtitlesButton
          ? {
              subtitlesVisible,
              toggleSubtitles: (newSubtitlesVisible) => {
                setSubtitlesVisible(newSubtitlesVisible)
              },
            }
          : undefined
      }
    />
  )
}
